import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Users,
  Truck,
  Package,
  AlertTriangle,
  FileText,
  ShoppingCart,
  ArrowRight,
  TrendingUp,
  Activity,
  HeartHandshake,
  Calendar,
  Lock,
  Unlock,
  Shield,
  MonitorPlay
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORS = ['#0d9488', '#f59e0b', '#3b82f6', '#ef4444', '#64748b'];

const StatCard = ({ title, value, icon: Icon, trend, color = 'teal', link }) => {
  const colorClasses = {
    teal: 'bg-teal-50 text-teal-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    blue: 'bg-blue-50 text-blue-600',
  };

  return (
    <Card className="card-hover">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
            <p className="text-3xl font-bold text-slate-900 font-['Manrope']">{value}</p>
            {trend && (
              <div className="flex items-center gap-1 mt-2 text-sm">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                <span className="text-emerald-600 font-medium">{trend}</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
        {link && (
          <Link to={link} className="mt-4 flex items-center text-sm text-teal-600 hover:text-teal-700 font-medium">
            Ver detalles
            <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        )}
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [deliveriesByMonth, setDeliveriesByMonth] = useState([]);
  const [deliveriesByType, setDeliveriesByType] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [workerStats, setWorkerStats] = useState({ workers: [], period_label: 'Este Mes' });
  const [workerPeriod, setWorkerPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  
  // Desk State for Social Workers
  const [myDesk, setMyDesk] = useState(null);
  
  // Desks State for Admins
  const [adminDesks, setAdminDesks] = useState([]);

  const isAdmin = user?.role === 'admin';
  const isSocialWorker = user?.role === 'social_worker';

  // Fetch general stats
  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises = [
          api.get('/dashboard/stats'),
          api.get('/dashboard/deliveries-by-month'),
          api.get('/dashboard/deliveries-by-type'),
          api.get('/dashboard/recent-activity'),
        ];
        
        if (isSocialWorker) {
          promises.push(api.get('/desks/me').catch(() => ({ data: null })));
        }

        const responses = await Promise.all(promises);

        setStats(responses[0].data);
        setDeliveriesByMonth(responses[1].data);
        setDeliveriesByType(responses[2].data);
        setRecentActivity(responses[3].data);
        
        if (isSocialWorker && responses[4] && responses[4].data) {
          setMyDesk(responses[4].data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isSocialWorker]);

  // Fetch admin specific desks
  useEffect(() => {
    if (!isAdmin) return;
    const fetchAdminDesks = async () => {
      try {
        const res = await api.get('/desks/');
        setAdminDesks(res.data);
      } catch (err) {
        console.error('Error fetching admin desks:', err);
      }
    };
    fetchAdminDesks();
    
    // Auto-refresh desks every 15 secs for monitoring
    const interval = setInterval(fetchAdminDesks, 15000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  // Fetch worker stats when period changes (admin only)
  useEffect(() => {
    const fetchWorkerStats = async () => {
      if (!isAdmin) return;
      
      try {
        const response = await api.get(`/dashboard/stats-by-worker?period=${workerPeriod}`);
        setWorkerStats(response.data);
      } catch (error) {
        console.error('Error fetching worker stats:', error);
      }
    };

    fetchWorkerStats();
  }, [isAdmin, workerPeriod]);

  const toggleDeskStatus = async () => {
    if (!myDesk) return;
    try {
      const newStatus = !myDesk.is_open;
      const res = await api.patch(`/desks/${myDesk._id}/status?is_open=${newStatus}`);
      setMyDesk(res.data);
      toast.success(newStatus ? 'Caja PAC Abierta' : 'Caja PAC Cerrada');
    } catch (error) {
      toast.error('Error al cambiar el estado de la caja');
    }
  };
  
  const getWorkerName = (id) => {
    const worker = workerStats.workers?.find(w => w.user_id === id);
    return worker ? worker.name : 'Sin asignar';
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-36 bg-slate-200 rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-80 bg-slate-200 rounded-xl" />
          <div className="h-80 bg-slate-200 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Resumen de la gestión social municipal
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button asChild className="bg-teal-600 hover:bg-teal-700 shadow-lg shadow-teal-600/20">
            <Link to="/attention" data-testid="new-attention-btn">
              <HeartHandshake className="w-4 h-4 mr-2" />
              Nueva Atención
            </Link>
          </Button>
        </div>
      </div>
      
      {/* Social Worker Desk Widget */}
      {isSocialWorker && myDesk && (
        <Card className={`border-l-4 ${myDesk.is_open ? 'border-l-emerald-500' : 'border-l-slate-400'}`}>
          <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-slate-900">{myDesk.name}</h3>
              <p className="text-sm text-slate-500">
                Estado: {' '}
                <span className={myDesk.is_open ? 'text-emerald-600 font-medium' : 'text-slate-500 font-medium'}>
                  {myDesk.is_open ? 'Abierta para atenciones' : 'Cerrada'}
                </span>
              </p>
            </div>
            <Button 
              onClick={toggleDeskStatus} 
              className={myDesk.is_open ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
            >
              {myDesk.is_open ? (
                <><Lock className="w-4 h-4 mr-2" /> Cerrar Caja PAC</>
              ) : (
                <><Unlock className="w-4 h-4 mr-2" /> Abrir Caja PAC</>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {isSocialWorker && !myDesk && !loading && (
        <Card className="border-l-4 border-l-amber-500 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-amber-800">
              <AlertTriangle className="w-6 h-6" />
              <div>
                <p className="font-semibold">Sin Caja PAC asignada</p>
                <p className="text-sm">Contacte a su administrador para que le asigne una caja de atención.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin Monitoring Widget */}
      {isAdmin && adminDesks.length > 0 && (
        <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white border-none shadow-xl">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <MonitorPlay className="w-5 h-5 text-teal-400" />
                Monitor de Cajas en Vivo
              </CardTitle>
              <CardDescription className="text-slate-400 mt-1">Estado de atención de PACs en tiempo real</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              {adminDesks.map(desk => (
                <div key={desk._id} className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/10 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-slate-100">{desk.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="relative flex h-3 w-3">
                          {desk.is_open && (
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                          )}
                          <span className={`relative inline-flex rounded-full h-3 w-3 ${desk.is_open ? 'bg-emerald-500' : 'bg-slate-500'}`}></span>
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between items-end mt-2">
                      <div className="text-sm text-slate-300">
                        {desk.assigned_worker_id ? getWorkerName(desk.assigned_worker_id) : 'Sin asignar'}
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-teal-300">{desk.today_attentions || 0}</div>
                        <div className="text-xs text-slate-400">Atenciones hoy</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <StatCard
          title="Beneficiarios Activos"
          value={stats?.active_beneficiaries || 0}
          icon={Users}
          color="teal"
          link="/beneficiaries"
          trend={stats?.total_beneficiaries ? `${stats.total_beneficiaries} total` : null}
        />
        <StatCard
          title="Entregas del Mes"
          value={stats?.monthly_deliveries || 0}
          icon={Truck}
          color="blue"
          link="/deliveries"
          trend={stats?.total_deliveries ? `${stats.total_deliveries} total` : null}
        />
        <StatCard
          title="Productos con Stock Bajo"
          value={stats?.low_stock_count || 0}
          icon={Package}
          color="amber"
          link="/inventory"
        />
        <StatCard
          title="Stock Crítico"
          value={stats?.critical_stock_count || 0}
          icon={AlertTriangle}
          color="red"
          link="/inventory"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Deliveries by Month */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope']">
              Entregas por Mes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {deliveriesByMonth.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={deliveriesByMonth}>
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const [year, month] = value.split('-');
                      const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
                      return months[parseInt(month) - 1] || value;
                    }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => [value, 'Entregas']}
                    labelFormatter={(label) => {
                      const [year, month] = label.split('-');
                      const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
                      return `${months[parseInt(month) - 1]} ${year}`;
                    }}
                  />
                  <Bar dataKey="count" fill="#0d9488" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                No hay datos de entregas
              </div>
            )}
          </CardContent>
        </Card>

        {/* Deliveries by Type */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope']">
              Entregas por Tipo de Ayuda
            </CardTitle>
          </CardHeader>
          <CardContent>
            {deliveriesByType.length > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={250}>
                  <PieChart>
                    <Pie
                      data={deliveriesByType}
                      dataKey="count"
                      nameKey="type"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {deliveriesByType.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [value, 'Entregas']} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {deliveriesByType.slice(0, 5).map((item, index) => (
                    <div key={item.type} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-sm text-slate-600 flex-1">{item.type}</span>
                      <span className="text-sm font-semibold text-slate-900">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                No hay datos de entregas
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold font-['Manrope']">
              Actividad Reciente
            </CardTitle>
            <Activity className="w-5 h-5 text-slate-400" />
          </CardHeader>
          <CardContent>
            {recentActivity.length > 0 ? (
              <div className="space-y-4">
                {recentActivity.map((activity, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-3 pb-4 border-b border-slate-100 last:border-0 last:pb-0"
                  >
                    <div className={`p-2 rounded-lg ${
                      activity.type === 'delivery' 
                        ? 'bg-blue-50 text-blue-600'
                        : 'bg-emerald-50 text-emerald-600'
                    }`}>
                      {activity.type === 'delivery' ? (
                        <Truck className="w-4 h-4" />
                      ) : (
                        <Users className="w-4 h-4" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {activity.description}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatDate(activity.date)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-40 flex items-center justify-center text-slate-400">
                No hay actividad reciente
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope']">
              Pendientes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Link 
              to="/purchase-orders"
              className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <ShoppingCart className="w-5 h-5 text-amber-500" />
                <span className="text-sm font-medium text-slate-700">Órdenes de Compra</span>
              </div>
              <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                {stats?.pending_orders || 0}
              </Badge>
            </Link>
            <Link 
              to="/invoices"
              className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-blue-500" />
                <span className="text-sm font-medium text-slate-700">Facturas Pendientes</span>
              </div>
              <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                {stats?.pending_invoices || 0}
              </Badge>
            </Link>
            <Link 
              to="/inventory"
              className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <span className="text-sm font-medium text-slate-700">Stock Crítico</span>
              </div>
              <Badge variant="secondary" className="bg-red-100 text-red-700">
                {stats?.critical_stock_count || 0}
              </Badge>
            </Link>
            <Link
              to="/inventory-general"
              className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-teal-500" />
                <span className="text-sm font-medium text-slate-700">Inventario General</span>
              </div>
              <Badge variant="secondary" className="bg-teal-100 text-teal-700">
                Ver
              </Badge>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Worker Stats - Admin Only */}
      {isAdmin && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold font-['Manrope']">
              Atenciones por Asistente Social ({workerStats.period_label || 'Este Mes'})
            </CardTitle>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <Select value={workerPeriod} onValueChange={setWorkerPeriod}>
                <SelectTrigger className="w-32 bg-slate-50" data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Hoy</SelectItem>
                  <SelectItem value="month">Este Mes</SelectItem>
                  <SelectItem value="year">Este Año</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Asistente Social</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-600">
                      Atenciones {workerPeriod === 'day' ? 'Hoy' : workerPeriod === 'year' ? 'del Año' : 'del Mes'}
                    </th>
                    <th className="text-center py-3 px-4 font-medium text-slate-600">
                      Entregas {workerPeriod === 'day' ? 'Hoy' : workerPeriod === 'year' ? 'del Año' : 'del Mes'}
                    </th>
                    <th className="text-center py-3 px-4 font-medium text-slate-600">Total Histórico</th>
                  </tr>
                </thead>
                <tbody>
                  {workerStats.workers?.map((worker) => (
                    <tr key={worker.user_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={worker.picture} alt={worker.name} />
                            <AvatarFallback className="bg-teal-100 text-teal-700 text-sm font-semibold">
                              {worker.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'AS'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-slate-900">{worker.name}</p>
                            <p className="text-xs text-slate-500">{worker.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-teal-100 text-teal-700 font-bold">
                          {worker.period_attentions}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-bold">
                          {worker.period_deliveries}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="text-sm">
                          <span className="font-semibold text-slate-900">{worker.total_attentions}</span>
                          <span className="text-slate-400"> atenciones</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!workerStats.workers || workerStats.workers.length === 0) && (
                <div className="text-center py-8 text-slate-400">
                  No hay asistentes sociales registrados
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
